import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { useCallback } from 'react'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import useSessionStore from '@/stores/useSessionStore'

import { AddressPoint, addressPointFragment } from './useAddressPointApi'

interface ShipPoint {
  address1: string
  attentionName?: string
  city: string
  companyName?: string
  countryCode: string
  facilityId?: string
  state: string
  zip5: string
}

interface ShipRoute {
  origin: ShipPoint
  destination: ShipPoint
}

interface Product {
  productLine: {
    name: string
  }
  sku: string
}

export interface Order {
  createdAt: string
  id: string
  legs: {
    pickup: boolean
    state: string
    shipRoute: ShipRoute
    shipments: {
      arrivalDate: string
      product?: Product
      shipDate: string
      trackingId?: string
    }[]
  }[]
  state: string
  totalPriceCents: number
  user: {
    firstName: string
    lastName: string
  }
}

export interface OrderConnection {
  nodes: Order[]
  pageInfo: {
    orderEndCursor: string | null
    orderHasNextPage: boolean
  }
}

export interface User {
  addresses?: AddressPoint[]
  clubName: string
  countryCode: string
  email: string
  firstName: string
  id: string
  lastName: string
  pgaNumber?: string
  phoneNumber: string
  role: string
  smsTrackingOptin: boolean
  smsTrackingPhoneNumber?: string
  smsTrackingTermsAccepted: boolean
}

const shipPointFragment = gql`
  fragment shipPoint on ShipPoint {
    address1
    attentionName
    city
    companyName
    countryCode
    facilityId
    state
    zip5
  }
`

const shipRouteFragment = gql`
  fragment shipRoute on ShipRoute {
    origin {
      ...shipPoint
    }
    destination {
      ...shipPoint
    }
  }
  ${shipPointFragment}
`

const productFragment = gql`
  fragment product on Product {
    ... on LabelProduct {
      productLine {
        name
      }
      sku
    }
  }
`

const orderFragment = gql`
  fragment order on Order {
    createdAt
    id: orderId
    legs {
      pickup
      state
      shipRoute {
        ...shipRoute
      }
      shipments {
        arrivalDate
        product {
          ...product
        }
        shipDate
        trackingId
      }
    }
    state
    totalPriceCents
    user {
      firstName
      lastName
    }
  }
  ${shipRouteFragment}
  ${productFragment}
`

export const ordersFragment = gql`
  fragment orders on OrderConnection {
    nodes {
      ...order
    }
    pageInfo {
      orderEndCursor: endCursor
      orderHasNextPage: hasNextPage
    }
  }
  ${orderFragment}
`

export const userFragment = gql`
  fragment user on User {
    addresses {
      ...addressPoint
    }
    clubName
    countryCode
    email
    firstName
    id
    lastName
    pgaNumber
    phoneNumber
    role
    smsTrackingOptin
    smsTrackingPhoneNumber
    smsTrackingTermsAccepted
  }
  ${addressPointFragment}
`

export const useUserApi = (): UseQueryResult<User, Error> => {
  const client = useGraphQLClient()
  const userId = useSessionStore((state) => state.userId)

  const query = gql`
    query GetUser {
      user {
        ...user
      }
    }
    ${userFragment}
  `

  const queryFn = useCallback(
    (): Promise<User> => client.request<{ user: User }>(query).then(({ user }) => user),
    [client, query],
  )

  return useQuery<User, Error>({ enabled: !!userId, queryKey: ['user', userId], queryFn })
}
